import React, { useRef } from 'react';
import { generateUUID } from '@/shared/utils/uuid';
import {
  Bars3Icon,
  XMarkIcon,
  ChevronDownIcon,
  InformationCircleIcon as InformationCircleOutlineIcon,
} from '@heroicons/react/24/outline';
import { InformationCircleIcon } from '@heroicons/react/24/solid';
import { TextField } from '@/shared/components/TextField';
import { Checkbox } from '@/shared/components/Checkbox';
import { Dropdown, DropDownButton } from '@/shared/components/Dropdown';
import { Tooltip } from '@/shared/components/Tooltip';
import StepperField from '../../components/StepperField';
import type { FormField, FieldOption, FieldType } from '../../types';
import {
  TASK_FORM_LABELS,
  FIELD_TYPE_OPTIONS,
  ACCEPTED_FILE_TYPE_OPTIONS,
} from '../../constants/labels';

const FIELD_TYPE_ITEMS = FIELD_TYPE_OPTIONS.map((opt) => ({
  id: opt.value,
  label: opt.label,
}));
const FILE_TYPE_ITEMS = ACCEPTED_FILE_TYPE_OPTIONS.map((t) => ({
  id: t,
  label: t,
}));

// ─── Options list (dropdown / radio / checkbox) ───────────────────────────────

const OptionsList: React.FC<{
  options: FieldOption[];
  multiSelect: boolean;
  onOptionsChange: (opts: FieldOption[]) => void;
  onMultiSelectChange: (v: boolean) => void;
  fieldType: FieldType;
  sectionIndex: number;
  fieldIndex: number;
  minOptionsError?: string;
  optionErrors?: Record<string, { label?: string }>;
}> = ({
  options,
  multiSelect,
  onOptionsChange,
  onMultiSelectChange,
  fieldType,
  sectionIndex,
  fieldIndex,
  minOptionsError,
  optionErrors,
}) => {
  const dragIndexRef = useRef<number | null>(null);
  const dragFromHandleRef = useRef(false);

  const updateOption = (idx: number, label: string) => {
    const updated = options.map((o, i) =>
      i === idx
        ? {
            ...o,
            label,
            value: label.toLowerCase().replace(/\s+/g, '_') || o.value,
          }
        : o
    );
    onOptionsChange(updated);
    // Auto-add a new blank row when the user types in the last slot (max 10)
    if (idx === options.length - 1 && label && options.length < 10) {
      onOptionsChange([
        ...updated,
        { id: generateUUID(), label: '', value: `option_${Date.now()}` },
      ]);
    }
  };

  const removeOption = (idx: number) => {
    onOptionsChange(options.filter((_, i) => i !== idx));
  };

  const onDrop = (toIdx: number) => {
    const from = dragIndexRef.current;
    if (from === null || from === toIdx) {
      return;
    }
    const reordered = [...options];
    const a = reordered[from];
    const b = reordered[toIdx];
    if (!a || !b) {
      return;
    }
    reordered[from] = b;
    reordered[toIdx] = a;
    onOptionsChange(reordered);
    dragIndexRef.current = null;
  };

  return (
    <div className="flex flex-col gap-3 mt-2">
      <div className="flex flex-col gap-1">
        <p
          className="font-medium text-[#474A4C]"
          style={{ fontSize: 16, lineHeight: '24px', letterSpacing: '0.15px' }}
        >
          {TASK_FORM_LABELS.OPTIONS_LABEL}
        </p>
        <p className="text-xs text-[#474A4C]">
          {TASK_FORM_LABELS.OPTIONS_DESC}
        </p>
      </div>
      {fieldType === 'dropdown' && (
        <Checkbox
          checked={multiSelect}
          onChange={onMultiSelectChange}
          label={TASK_FORM_LABELS.MULTI_SELECT}
          labelClassName="text-bodyMedium !font-normal text-[#181A1B]"
          testId="dropdown-multi-select"
        />
      )}

      <div className="flex flex-col gap-3">
        {options.map((opt, idx) => (
          <div
            key={opt.id}
            className="flex items-center gap-2"
            draggable
            onDragStart={(e) => {
              if (!dragFromHandleRef.current) {
                e.preventDefault();
                return;
              }
              e.stopPropagation();
              dragFromHandleRef.current = false;
              dragIndexRef.current = idx;
            }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => onDrop(idx)}
          >
            <Tooltip
              content={TASK_FORM_LABELS.TOOLTIP_REORDER_OPTION}
              forceShow
              side="top"
              align="center"
              triggerWrapContent
              contentMinWidth={160}
            >
              <span
                className="shrink-0 cursor-grab"
                onMouseDown={() => {
                  dragFromHandleRef.current = true;
                }}
              >
                <Bars3Icon className="w-6 h-6 text-[#474A4C]" />
              </span>
            </Tooltip>
            <span className="text-bodyMedium text-[#181A1B] w-5 shrink-0 text-right">
              {idx + 1}.
            </span>
            <div className="flex-1">
              <TextField
                label={`Opcja ${idx + 1}`}
                value={opt.label}
                onChange={(e) => updateOption(idx, e.target.value)}
                maxLength={200}
                fullWidth
                error={Boolean(optionErrors?.[opt.id]?.label)}
                errorMessage={optionErrors?.[opt.id]?.label ?? ''}
                dataTestId={`option-input-${sectionIndex}-${fieldIndex}-${idx}`}
              />
            </div>
            <Tooltip
              content={TASK_FORM_LABELS.TOOLTIP_DELETE_OPTION}
              forceShow
              side="top"
              align="center"
              triggerWrapContent
              triggerTabIndex={-1}
              contentMinWidth={100}
            >
              <button
                type="button"
                onClick={
                  options.length === 1 ? undefined : () => removeOption(idx)
                }
                disabled={options.length === 1}
                className={`shrink-0 ${options.length === 1 ? 'text-[#DCDDE0] cursor-not-allowed' : 'text-[#474A4C] cursor-pointer'}`}
                aria-label="Usuń opcję"
                data-testid={`option-delete-${sectionIndex}-${fieldIndex}-${idx}`}
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </Tooltip>
          </div>
        ))}
      </div>

      {minOptionsError && (
        <p className="text-xs text-error-40">{minOptionsError}</p>
      )}
    </div>
  );
};

// ─── Number range config ──────────────────────────────────────────────────────

const NumberRangeConfig: React.FC<{
  min: number | null;
  max: number | null;
  unit: string;
  onMinChange: (v: number | null) => void;
  onMaxChange: (v: number | null) => void;
  onUnitChange: (v: string) => void;
  sectionIndex: number;
  fieldIndex: number;
  minError?: string;
  maxError?: string;
  unitError?: string;
}> = ({
  min,
  max,
  unit,
  onMinChange,
  onMaxChange,
  onUnitChange,
  sectionIndex,
  fieldIndex,
  minError,
  maxError,
  unitError,
}) => {
  const [alertDismissed, setAlertDismissed] = React.useState(false);

  return (
    <div className="flex flex-col gap-3 mt-2">
      <div className="flex items-center gap-2">
        <span className="text-titleMedium text-[#474A4C]">
          {TASK_FORM_LABELS.NUMBER_RANGE_SECTION}
        </span>
        {alertDismissed && (
          <Tooltip
            content={TASK_FORM_LABELS.NUMBER_RANGE_TOOL_TIP_INFO}
            side="bottom"
            align="center"
            forceShow
            multiline
            triggerWrapContent
            triggerTabIndex={-1}
            contentMinWidth={160}
          >
            <button
              type="button"
              onClick={() => setAlertDismissed(false)}
              className="flex items-center text-[#181A1B] hover:text-[#474A4C] shrink-0 cursor-pointer"
              aria-label="Pokaż informację o zakresie"
            >
              <InformationCircleOutlineIcon className="w-5 h-5" />
            </button>
          </Tooltip>
        )}
      </div>

      {!alertDismissed && (
        <div className="flex items-center gap-2 bg-[#EAF3FF] rounded py-2 pl-6 pr-4 min-w-[256px] w-[89%] min-h-[48px]">
          <InformationCircleIcon className="w-5 h-5 text-[#1E7CE8] shrink-0" />
          <p className="text-sm text-[#1E7CE8] flex-1">
            <strong>Note:</strong> {TASK_FORM_LABELS.NUMBER_RANGE_ALERT}
          </p>
          <button
            type="button"
            onClick={() => setAlertDismissed(true)}
            className="text-[#1E7CE8] shrink-0"
            aria-label="Zamknij"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="flex items-start gap-3 w-[89%]">
        <div className="flex-1 min-w-0">
          <StepperField
            label={TASK_FORM_LABELS.MIN_VALUE}
            value={min}
            onChange={onMinChange}
            width="100%"
            dataTestId={`number-range-min-${sectionIndex}-${fieldIndex}`}
            {...(minError !== undefined && {
              error: true,
              errorMessage: minError,
            })}
          />
        </div>

        <div className="flex items-center shrink-0" style={{ height: 56 }}>
          <span className="text-sm text-[#474A4C]">
            {TASK_FORM_LABELS.TO_LABEL}
          </span>
        </div>

        <div className="flex-1 min-w-0">
          <StepperField
            label={TASK_FORM_LABELS.MAX_VALUE}
            value={max}
            onChange={onMaxChange}
            width="100%"
            dataTestId={`number-range-max-${sectionIndex}-${fieldIndex}`}
            {...(maxError !== undefined && {
              error: true,
              errorMessage: maxError,
            })}
          />
        </div>

        <div className="flex-1 min-w-0">
          <TextField
            label={TASK_FORM_LABELS.UNIT_LABEL}
            value={unit}
            onChange={(e) => onUnitChange(e.target.value.slice(0, 10))}
            maxLength={10}
            width="100%"
            error={Boolean(unitError)}
            errorMessage={unitError ?? ''}
            dataTestId={`number-range-unit-${sectionIndex}-${fieldIndex}`}
          />
        </div>
      </div>
    </div>
  );
};

// ─── Attachment config ────────────────────────────────────────────────────────

const AttachmentConfig: React.FC<{
  acceptedFileTypes: string[];
  maxFileSizeMb: number | null;
  maxFiles: number | null;
  onFileTypesChange: (types: string[]) => void;
  onMaxFileSizeMbChange: (v: number | null) => void;
  onMaxFilesChange: (v: number | null) => void;
  sectionIndex: number;
  fieldIndex: number;
  fileTypesError?: string;
  maxSizeError?: string;
  maxFilesError?: string;
}> = ({
  acceptedFileTypes,
  maxFileSizeMb,
  maxFiles,
  onFileTypesChange,
  onMaxFileSizeMbChange,
  onMaxFilesChange,
  sectionIndex,
  fieldIndex,
  fileTypesError,
  maxSizeError,
  maxFilesError,
}) => {
  const toggleType = (type: string) => {
    if (acceptedFileTypes.includes(type)) {
      onFileTypesChange(acceptedFileTypes.filter((t) => t !== type));
    } else {
      onFileTypesChange([...acceptedFileTypes, type]);
    }
  };

  return (
    <div className="flex flex-col gap-3 mt-2 w-[90%]">
      <div className="flex flex-col gap-1">
        <p className="text-base font-medium leading-6 tracking-[0.15px] text-[#474A4C]">
          {TASK_FORM_LABELS.ATTACHMENT_CONFIG_LABEL}
        </p>
        <p className="text-sm font-normal leading-5 tracking-[0.25px] text-[#474A4C]">
          {TASK_FORM_LABELS.ATTACHMENT_CONFIG_DESC}
        </p>
      </div>

      {/* File types multi-select */}
      <div>
        <Dropdown
          items={FILE_TYPE_ITEMS}
          selectedId={acceptedFileTypes}
          onItemClick={toggleType}
          multiSelect
          showCheckboxes
          menuFullWidth
          placement="bottom-start"
          autoMaxHeight
          error={Boolean(fileTypesError)}
          trigger={({ open, toggle, error: hasErr }) => {
            const isFloated = open || acceptedFileTypes.length > 0;
            return (
              <div
                className="relative w-full flex items-center gap-2 cursor-pointer"
                style={{ minHeight: 44 }}
                onClick={toggle}
                data-testid={`attachment-file-types-${sectionIndex}-${fieldIndex}`}
              >
                <span
                  className="absolute left-0 pointer-events-none select-none"
                  style={{
                    top: isFloated ? 2 : '50%',
                    transform: isFloated ? 'none' : 'translateY(-50%)',
                    fontSize: isFloated ? 12 : 16,
                    lineHeight: '16px',
                    color: isFloated ? '#6a7278' : '#9B9EA0',
                    transition:
                      'top 200ms ease-out, font-size 200ms ease-out, color 200ms ease-out, transform 200ms ease-out',
                  }}
                >
                  {TASK_FORM_LABELS.ACCEPTED_FILE_TYPES}
                </span>
                <div
                  className="flex flex-wrap gap-1.5 flex-1 min-w-0"
                  style={{ paddingTop: isFloated ? 18 : 0 }}
                >
                  {acceptedFileTypes.map((type) => (
                    <span
                      key={type}
                      className="inline-flex items-center gap-1 bg-[#EAF3FF] text-[#1665C2] text-xs px-2 h-7 rounded"
                    >
                      {type}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleType(type);
                        }}
                        aria-label={`Usuń ${type}`}
                        className="cursor-pointer"
                      >
                        <XMarkIcon className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <ChevronDownIcon
                  className={`w-5 h-5 shrink-0 transition-transform duration-150 ${open ? 'rotate-180' : ''} ${hasErr ? 'text-error-40' : 'text-[#474A4C]'}`}
                />
              </div>
            );
          }}
        />
        {fileTypesError && (
          <p className="mt-1 px-4 text-xs text-error-40">{fileTypesError}</p>
        )}
      </div>

      <div className="flex gap-4">
        <div className="flex-1">
          <StepperField
            label={TASK_FORM_LABELS.MAX_FILE_SIZE}
            value={maxFileSizeMb}
            onChange={onMaxFileSizeMbChange}
            min={1}
            max={5}
            width="100%"
            dataTestId={`attachment-max-size-${sectionIndex}-${fieldIndex}`}
            {...(maxSizeError !== undefined && {
              error: true,
              errorMessage: maxSizeError,
            })}
          />
        </div>
        <div className="flex-1">
          <StepperField
            label={TASK_FORM_LABELS.MAX_FILES}
            value={maxFiles}
            onChange={onMaxFilesChange}
            min={1}
            max={10}
            width="100%"
            dataTestId={`attachment-max-files-${sectionIndex}-${fieldIndex}`}
            {...(maxFilesError !== undefined && {
              error: true,
              errorMessage: maxFilesError,
            })}
          />
        </div>
      </div>
    </div>
  );
};

// ─── FieldEditor ──────────────────────────────────────────────────────────────

export interface FieldEditorProps {
  field: FormField;
  sectionIndex: number;
  fieldIndex: number;
  onChange: (updated: FormField) => void;
  onRemove: () => void;
  isOnly?: boolean;
  labelError?: string;
  typeError?: string;
  minOptionsError?: string;
  optionErrors?: Record<string, { label?: string }>;
  rangeMinError?: string;
  rangeMaxError?: string;
  rangeUnitError?: string;
  attachmentFileTypesError?: string;
  attachmentMaxSizeError?: string;
  attachmentMaxFilesError?: string;
  isDragOver?: boolean;
  onDragEnterField?: () => void;
  onDragLeaveField?: (e: React.DragEvent) => void;
  onDragStart?: () => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDrop?: () => void;
  onDragEnd?: () => void;
}

const FieldEditor: React.FC<FieldEditorProps> = ({
  field,
  sectionIndex,
  fieldIndex,
  onChange,
  onRemove,
  isOnly = false,
  labelError,
  typeError,
  minOptionsError,
  optionErrors,
  rangeMinError,
  rangeMaxError,
  rangeUnitError,
  attachmentFileTypesError,
  attachmentMaxSizeError,
  attachmentMaxFilesError,
  isDragOver = false,
  onDragEnterField,
  onDragLeaveField,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
}) => {
  const dragFromHandleRef = useRef(false);

  const hasOptions =
    field.type === 'dropdown' ||
    field.type === 'radio' ||
    field.type === 'checkbox';
  const isNumberRange = field.type === 'number_range';
  const isAttachment = field.type === 'attachment';

  const handleTypeChange = (type: FieldType) => {
    const base: FormField = { ...field, type, multiSelect: false };
    if (type === 'dropdown' || type === 'radio' || type === 'checkbox') {
      base.options = [{ id: generateUUID(), label: '', value: 'option_1' }];
    } else {
      delete base.options;
    }
    if (type === 'attachment') {
      base.acceptedFileTypes = base.acceptedFileTypes ?? [];
      base.maxFileSizeMb = base.maxFileSizeMb ?? 5;
      base.maxFiles = base.maxFiles ?? 5;
    }
    onChange(base);
  };

  return (
    <div
      className="flex items-start gap-2"
      draggable
      onDragStart={(e) => {
        if (!dragFromHandleRef.current) {
          e.preventDefault();
          return;
        }
        e.stopPropagation();
        dragFromHandleRef.current = false;

        const typeLabel =
          FIELD_TYPE_OPTIONS.find((o) => o.value === field.type)?.label ?? '';

        const ghost = document.createElement('div');
        ghost.style.cssText =
          'position:fixed;top:-9999px;left:0;width:360px;background:white;border:1px solid #DCDDE0;border-radius:12px;overflow:hidden;box-shadow:0 4px 12px rgba(0,0,0,0.12);pointer-events:none;';

        const row = document.createElement('div');
        row.style.cssText =
          'display:flex;align-items:center;gap:12px;padding:10px 16px;';

        const labelWrap = document.createElement('div');
        labelWrap.style.cssText = 'flex:1;min-width:0;';
        const labelCaption = document.createElement('p');
        labelCaption.style.cssText =
          'font-size:11px;color:#878B8E;margin:0 0 2px;';
        labelCaption.textContent = TASK_FORM_LABELS.FIELD_LABEL_PLACEHOLDER;
        const labelValue = document.createElement('p');
        labelValue.style.cssText =
          'font-size:14px;font-weight:500;color:#474A4C;margin:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;';
        labelValue.textContent =
          field.label.trim() || TASK_FORM_LABELS.FIELD_LABEL_PLACEHOLDER;
        labelWrap.appendChild(labelCaption);
        labelWrap.appendChild(labelValue);

        const typeWrap = document.createElement('div');
        typeWrap.style.cssText = 'flex:1;min-width:0;';
        const typeCaption = document.createElement('p');
        typeCaption.style.cssText =
          'font-size:11px;color:#878B8E;margin:0 0 2px;';
        typeCaption.textContent = TASK_FORM_LABELS.FIELD_TYPE_LABEL;
        const typeValue = document.createElement('p');
        typeValue.style.cssText =
          'font-size:14px;color:#474A4C;margin:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;';
        typeValue.textContent = typeLabel;
        typeWrap.appendChild(typeCaption);
        typeWrap.appendChild(typeValue);

        const optWrap = document.createElement('div');
        optWrap.style.cssText =
          'display:flex;align-items:center;gap:6px;flex-shrink:0;';
        const optBox = document.createElement('div');
        optBox.style.cssText = `width:14px;height:14px;border-radius:3px;border:1.5px solid ${!field.required ? '#2A4455' : '#DCDDE0'};background:${!field.required ? '#2A4455' : 'white'};flex-shrink:0;`;
        const optText = document.createElement('span');
        optText.style.cssText = 'font-size:12px;color:#474A4C;';
        optText.textContent = TASK_FORM_LABELS.FIELD_OPTIONAL;
        optWrap.appendChild(optBox);
        optWrap.appendChild(optText);

        row.appendChild(labelWrap);
        row.appendChild(typeWrap);
        row.appendChild(optWrap);
        ghost.appendChild(row);

        document.body.appendChild(ghost);
        e.dataTransfer.setDragImage(ghost, 180, 20);
        requestAnimationFrame(() => document.body.removeChild(ghost));
        onDragStart?.();
      }}
      onDragEnter={onDragEnterField}
      onDragOver={onDragOver}
      onDragLeave={onDragLeaveField}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
    >
      {/* Drag handle */}
      <div className="shrink-0 flex items-center" style={{ height: 56 }}>
        <Tooltip
          content={TASK_FORM_LABELS.TOOLTIP_REORDER_FIELD}
          forceShow
          side="top"
          align="center"
          triggerWrapContent
          contentMinWidth={160}
        >
          <span
            className={`shrink-0 ${isOnly ? 'cursor-not-allowed' : 'cursor-grab'}`}
            onMouseDown={() => {
              if (!isOnly) {
                dragFromHandleRef.current = true;
              }
            }}
          >
            <Bars3Icon
              className={`w-6 h-6 ${isOnly ? 'text-[#DCDDE0]' : 'text-[#474A4C]'}`}
              aria-label="Przeciągnij pole"
            />
          </span>
        </Tooltip>
      </div>

      {/* Field row content */}
      <div
        className={`flex-1 flex flex-col gap-3 rounded-xl transition-colors ${isDragOver ? 'bg-[#EAF3FB] outline outline-1 outline-[#2A7ABA]' : ''}`}
      >
        <div className="flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <TextField
              label={TASK_FORM_LABELS.FIELD_LABEL_PLACEHOLDER}
              value={field.label}
              onChange={(e) =>
                onChange({ ...field, label: e.target.value.slice(0, 30) })
              }
              maxLength={30}
              fullWidth
              error={Boolean(labelError)}
              errorMessage={labelError ?? ''}
              dataTestId={`field-label-input-${sectionIndex}-${fieldIndex}`}
            />
          </div>

          <div className="flex-1 min-w-[180px]">
            <Dropdown
              items={FIELD_TYPE_ITEMS}
              selectedId={field.type}
              onItemClick={(id) => handleTypeChange(id as FieldType)}
              menuFullWidth
              placement="bottom-start"
              autoMaxHeight
              error={Boolean(typeError)}
              {...(typeError !== undefined && { errorMessage: typeError })}
              trigger={({ open, toggle, error: hasErr }) => (
                <DropDownButton
                  label={TASK_FORM_LABELS.FIELD_TYPE_LABEL}
                  value={
                    FIELD_TYPE_OPTIONS.find((o) => o.value === field.type)
                      ?.label ?? ''
                  }
                  open={open}
                  toggle={toggle}
                  error={hasErr}
                  data-testid={`field-type-button-${sectionIndex}-${fieldIndex}`}
                />
              )}
            />
          </div>

          <div className="shrink-0">
            <Checkbox
              checked={!field.required}
              onChange={(checked) => onChange({ ...field, required: !checked })}
              label={TASK_FORM_LABELS.FIELD_OPTIONAL}
              labelClassName="!font-normal leading-5 tracking-[0.25px]"
            />
          </div>

          <div className="shrink-0 flex items-center" style={{ height: 56 }}>
            <Tooltip
              content={TASK_FORM_LABELS.TOOLTIP_DELETE_FIELD}
              forceShow
              side="top"
              align="center"
              triggerWrapContent
              triggerTabIndex={-1}
              contentMinWidth={100}
            >
              <button
                type="button"
                onClick={isOnly ? undefined : onRemove}
                disabled={isOnly}
                className={`shrink-0 flex items-center ${isOnly ? 'text-[#DCDDE0] cursor-not-allowed' : 'text-[#474A4C] cursor-pointer'}`}
                aria-label="Usuń pole"
                data-testid={`delete-field-button-${sectionIndex}-${fieldIndex}`}
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </Tooltip>
          </div>
        </div>

        {/* Type-specific expansions */}
        {hasOptions && (
          <OptionsList
            options={field.options ?? []}
            multiSelect={field.multiSelect ?? false}
            onOptionsChange={(opts) => onChange({ ...field, options: opts })}
            onMultiSelectChange={(v) => onChange({ ...field, multiSelect: v })}
            fieldType={field.type as FieldType}
            sectionIndex={sectionIndex}
            fieldIndex={fieldIndex}
            {...(minOptionsError !== undefined && { minOptionsError })}
            {...(optionErrors !== undefined && { optionErrors })}
          />
        )}

        {isNumberRange && (
          <NumberRangeConfig
            min={field.min ?? null}
            max={field.max ?? null}
            unit={field.unit ?? ''}
            onMinChange={(v) => onChange({ ...field, min: v })}
            onMaxChange={(v) => onChange({ ...field, max: v })}
            onUnitChange={(v) => onChange({ ...field, unit: v })}
            sectionIndex={sectionIndex}
            fieldIndex={fieldIndex}
            {...(rangeMinError !== undefined && { minError: rangeMinError })}
            {...(rangeMaxError !== undefined && { maxError: rangeMaxError })}
            {...(rangeUnitError !== undefined && { unitError: rangeUnitError })}
          />
        )}

        {isAttachment && (
          <AttachmentConfig
            acceptedFileTypes={field.acceptedFileTypes ?? []}
            maxFileSizeMb={field.maxFileSizeMb ?? null}
            maxFiles={field.maxFiles ?? null}
            onFileTypesChange={(types) =>
              onChange({ ...field, acceptedFileTypes: types })
            }
            onMaxFileSizeMbChange={(v) =>
              onChange({ ...field, maxFileSizeMb: v })
            }
            onMaxFilesChange={(v) => onChange({ ...field, maxFiles: v })}
            sectionIndex={sectionIndex}
            fieldIndex={fieldIndex}
            {...(attachmentFileTypesError !== undefined && {
              fileTypesError: attachmentFileTypesError,
            })}
            {...(attachmentMaxSizeError !== undefined && {
              maxSizeError: attachmentMaxSizeError,
            })}
            {...(attachmentMaxFilesError !== undefined && {
              maxFilesError: attachmentMaxFilesError,
            })}
          />
        )}
      </div>
    </div>
  );
};

export default FieldEditor;
